import type { ChunkRecoveryLatch } from './ChunkRecoveryLatch'

// Message set matches known chunk/module-load failure signatures across browsers and bundlers.
const RECOVERABLE_CHUNK_ERROR_MESSAGES = [
  'importing a module script failed',
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'unable to preload css',
  'loading chunk',
  'chunk load failed',
  "couldn't resolve component"
] as const

const CHUNK_RECOVERY_LATCH_KEY = 'mealz:chunkRecovery'
const CHUNK_RECOVERY_BOOT_LATCH_KEY = 'mealz:chunkRecovery:boot'
const CHUNK_RECOVERY_MAX_ATTEMPTS = 1
const CHUNK_RECOVERY_WINDOW_MS = 5 * 60_000

/** Walks `.cause` up to 3 levels, since Nuxt/Vite wrap the real fetch failure. */
const collectChunkErrorMessages = (error: unknown): string => {
  const parts: string[] = []
  let current: unknown = error
  for (let depth = 0; depth < 3 && current instanceof Error; depth += 1) {
    parts.push(current.message)
    current = current.cause
  }
  return parts.join(' | ').toLowerCase()
}

const isRecoverableChunkError = (error: unknown): boolean => {
  const message = collectChunkErrorMessages(error)
  return (
    message.length > 0 &&
    RECOVERABLE_CHUNK_ERROR_MESSAGES.some((candidate) => message.includes(candidate))
  )
}

/**
 * Pure latch transition: a latch older than CHUNK_RECOVERY_WINDOW_MS (or absent)
 * starts a fresh sequence at 1; otherwise the count increments. Caller owns storage.
 */
const nextChunkRecoveryLatch = (
  existing: ChunkRecoveryLatch | null,
  now: number
): ChunkRecoveryLatch => {
  const withinWindow = existing !== null && now - existing.firstAt <= CHUNK_RECOVERY_WINDOW_MS
  return withinWindow
    ? { count: existing.count + 1, firstAt: existing.firstAt }
    : { count: 1, firstAt: now }
}

export const chunkRecovery = {
  RECOVERABLE_CHUNK_ERROR_MESSAGES,
  CHUNK_RECOVERY_LATCH_KEY,
  CHUNK_RECOVERY_BOOT_LATCH_KEY,
  CHUNK_RECOVERY_MAX_ATTEMPTS,
  CHUNK_RECOVERY_WINDOW_MS,
  collectChunkErrorMessages,
  isRecoverableChunkError,
  nextChunkRecoveryLatch
}
